import { Pages } from '@constants/routes.constant'
import React, { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes, useParams, useLocation } from 'react-router-dom'
import { useUser } from '@contexts/user.context'
import { useAuth } from '@contexts/auth.context'
import { Role } from '@enums/role.enum'
import LoadingTransition from '@components/loading/loading-transition'
import { defaultLocale, locales } from '../../i18n'
import { LanguageProvider } from '@contexts/language.context'
import { WebsiteSchema, OrganizationSchema } from '../seo/structured-data.component'

// Layout components
import TopNavigationBar from '@components/navigation/top.navigation'
import SettingsSideNavigationBar from '@components/navigation/settings-side.navigation'
import HelpWidget from '@components/help/help-widget'
import LogoLoader from '@components/loader/logo.loader'

// Lazy loaded pages
const AdminPage = lazy(() => import('@pages/admin.page'))
const LandingPage = lazy(() => import('@pages/landing.page'))
const SigninPage = lazy(() => import('@pages/signin.page'))
const SignupPage = lazy(() => import('@pages/signup.page'))
const ResetPasswordPage = lazy(() => import('@pages/reset-password.page'))
const VerficicationPage = lazy(() => import('@pages/verification.page'))
const DataPolicyPage = lazy(() => import('@pages/data-policy.page'))
const TermsOfUsePage = lazy(() => import('@pages/terms-of-use.page'))
const CheckoutPage = lazy(() => import('@pages/checkout.page'))
const HelpCenterRouter = lazy(() => import('@pages/help.page'))
const HomePage = lazy(() => import('@pages/home.page'))
const ConversationPage = lazy(() => import('@pages/conversation.page'))
const TemplatesPage = lazy(() => import('@pages/templates.page'))
const TagsPage = lazy(() => import('@pages/tags.page'))
const GeneralSettingsPage = lazy(() => import('@pages/settings/general.settings.page'))
const PlanSettingsPage = lazy(() => import('@pages/settings/plan.settings.page'))
const OverviewBillingSettingsPage = lazy(() => import('@pages/settings/billing/overview.billing.settings.page'))
const NewsletterUnsubscribePage = lazy(() => import('@pages/newsletter/unsubscribe.page'))
const PricingPage = lazy(() => import('@pages/pricing.page'))
const ContactPage = lazy(() => import('@pages/contact.page'))

// Layout wrappers
const PublicLayout = () => <Outlet />

const AuthenticatedLayout = () => (
  <div>
    <TopNavigationBar Routes={<Outlet />} />
    <HelpWidget />
  </div>
)

const SettingsLayout = () => <SettingsSideNavigationBar Routes={<Outlet />} />

// Route guards
const ProtectedRoute = () => {
  const { authTokens } = useAuth()
  return authTokens?.refreshToken ? <Outlet /> : <Navigate to={Pages.LANDING} />
}

const AdminRoute = () => {
  const { authTokens } = useAuth()
  const { user } = useUser()

  if (!authTokens?.refreshToken) {
    return <Navigate to={Pages.LANDING} />
  }

  if (user?.role !== Role.ADMIN) {
    return <Navigate to={Pages.HOME} />
  }

  return <Outlet />
}

const AnonymousRoute = () => {
  const { authTokens } = useAuth()
  const { user } = useUser()
  return user?.id && authTokens?.refreshToken ? <Navigate to={Pages.HOME} /> : <Outlet />
}

// Wrapper component for ConversationPage to get URL parameters
const ConversationPageWrapper = () => {
  const { conversationId } = useParams()
  return conversationId ? <ConversationPage conversationId={conversationId} /> : <Navigate to={Pages.HOME} />
}

// Redirect handler for non-language prefixed routes
const DefaultLanguageRedirect = () => {
  const location = useLocation()
  const path = location.pathname === '/' ? '' : location.pathname
  return <Navigate to={`/${defaultLocale}${path}`} replace />
}

// Support for nested routes by language prefix
const LocalizedRoutes = () => (
  <Routes>
    {/* Public routes */}
    <Route element={<AnonymousRoute />}>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path={Pages.SIGN_IN.replace('/', '')} element={<SigninPage />} />
        <Route path={Pages.SIGN_UP.replace('/', '')} element={<SignupPage />} />
        <Route path={Pages.VERIFICATION.replace('/', '')} element={<VerficicationPage />} />
        <Route path={Pages.RESET_PASSWORD.replace('/', '')} element={<ResetPasswordPage />} />
        <Route path={Pages.REQUEST_RESET_PASSWORD.replace('/', '')} element={<ResetPasswordPage />} />
        <Route path={Pages.DATA_POLICY.replace('/', '')} element={<DataPolicyPage />} />
        <Route path={Pages.TERMS_OF_USE.replace('/', '')} element={<TermsOfUsePage />} />
        <Route path={Pages.CHECKOUT.replace('/', '')} element={<CheckoutPage />} />
        <Route path={Pages.NEWSLETTER_UNSUBSCRIBE.replace('/', '')} element={<NewsletterUnsubscribePage />} />
        <Route path={Pages.PRICING.replace('/', '')} element={<PricingPage />} />
        <Route path={Pages.CONTACT.replace('/', '')} element={<ContactPage />} />

        {/* Temporary redirects */}
        <Route path={Pages.ABOUT.replace('/', '')} element={<Navigate to="/" replace />} />
        <Route path={Pages.FEATURES.replace('/', '')} element={<Navigate to="/" replace />} />
        <Route path={Pages.TEAM.replace('/', '')} element={<Navigate to="/" replace />} />
        <Route path={Pages.DEMO.replace('/', '')} element={<Navigate to="/" replace />} />
        <Route path={Pages.COOKIES.replace('/', '')} element={<Navigate to={Pages.DATA_POLICY.replace('/', '')} replace />} />
      </Route>
    </Route>

    {/* Help pages (accessible to both logged in and anonymous users) */}
    <Route path={`${Pages.HELP.replace('/', '')}/*`} element={<HelpCenterRouter />} />

    {/* Protected routes */}
    <Route element={<ProtectedRoute />}>
      <Route element={<AuthenticatedLayout />}>
        <Route path={Pages.HOME.replace('/', '')} element={<HomePage />} />
        <Route path={Pages.TEMPLATES.replace('/', '')} element={<TemplatesPage />} />
        <Route path={Pages.TAGS.replace('/', '')} element={<TagsPage />} />
        <Route path={`${Pages.CONVERSATIONS.replace('/', '')}/:conversationId`} element={<ConversationPageWrapper />} />

        {/* Settings routes */}
        <Route path={Pages.SETTINGS.replace('/', '')} element={<SettingsLayout />}>
          <Route index element={<GeneralSettingsPage />} />
          <Route path="plan" element={<PlanSettingsPage />} />
          <Route path="billing" element={<OverviewBillingSettingsPage />} />
        </Route>
      </Route>
    </Route>

    {/* Admin routes */}
    <Route element={<AdminRoute />}>
      <Route path={`${Pages.ADMIN.replace('/', '')}/*`} element={<AdminPage />} />
    </Route>

    {/* Catch-all redirect */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
)

const Router: React.FC = () => (
  <Suspense
    fallback={
      <div>
        <LoadingTransition>
          <LogoLoader />
        </LoadingTransition>
      </div>
    }
  >
    <LanguageProvider>
      <WebsiteSchema />
      <OrganizationSchema />
      <Routes>
        {/* Redirect root to language-prefixed route */}
        <Route path="/" element={<DefaultLanguageRedirect />} />
        
        {/* Language-specific routes */}
        {Object.keys(locales).map(lang => (
          <Route key={lang} path={`/${lang}/*`} element={<LocalizedRoutes />} />
        ))}
        
        {/* Legacy routes - redirect to default language */}
        <Route path="/*" element={<DefaultLanguageRedirect />} />
      </Routes>
    </LanguageProvider>
  </Suspense>
)

export default Router
